import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Snackbar,
  IconButton,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MuiAlert from "@mui/material/Alert";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import axios from "axios";
import EvaluationViewer from "./EvaluationViewer";

export default function ScriptValuation() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [startTime, setStartTime] = useState(null);
  // NEW STATE - course selection
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedCourseName, setSelectedCourseName] = useState(null);
  const [selectedCourseCode, setSelectedCourseCode] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const user = JSON.parse(sessionStorage.getItem("user"));
  const roleId = user.core_user_roles?.[0]?.role_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const showSnackbar = (msg, severity = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleStartEvaluation = (row) => {
    setStartTime(Date.now());
    setSelectedScript(row);
  };

  function getTimetableIdFromFile(pdfFileName) {
    if (!pdfFileName) return null;
    const match = pdfFileName.match(/HT-[^-]+-(\d+)\.pdf$/);
    return match ? Number(match[1]) : null;
  }

  // Fetch scripts
  const fetchScripts = async () => {
    setLoading(true);
    try {
      let data = null;
      if (roleId == 6) {
        data = await axios.get(
          `${API_BASE_URL}/api/script-valuation/userwise/${user.client_id}/${user.college_id}/${user.user_email}`
        );
      } else {
        data = await axios.get(
          `${API_BASE_URL}/api/script-valuation/collegewise/${user.client_id}/${user.college_id}`
        );
      }
      if (data.data.success) {
        const updatedRows = (data.data.data.items || []).map((item) => ({
          ...item,
          exam_course_id: item.allocation?.exam_course_id ?? null,
          timetable_id: getTimetableIdFromFile(item.script_pdf),
          course_code: item.allocation?.course?.course_code ?? null,
          course_name: item.allocation?.course?.course_name ?? null,
        }));
        setRows(updatedRows);
      } else {
        showSnackbar(data.message || "Failed to load scripts", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar("Error fetching script details", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScripts();
  }, []);

  // GROUP rows by exam_course_id
  const groupedCourses = useMemo(() => {
    const map = {};

    rows.forEach((r) => {
      if (!map[r.exam_course_id]) {
        map[r.exam_course_id] = {
          exam_course_id: r.exam_course_id,
          course_code: r.course_code,
          course_name: r.course_name,
          total: 0,
          pending: 0,
          completed: 0,
          verified: 0,
        };
      }

      map[r.exam_course_id].total += 1;

      switch (r.evaluation_status) {
        case "PENDING":
          map[r.exam_course_id].pending += 1;
          break;
        case "COMPLETED":
          map[r.exam_course_id].completed += 1;
          break;
        case "VERIFIED":
          map[r.exam_course_id].verified += 1;
          break;
        default:
          break;
      }
    });

    return Object.values(map);
  }, [rows]);

  // Filter scripts for selected course
  const courseScripts = useMemo(() => {
    if (!selectedCourse) return [];
    return rows.filter((r) => r.exam_course_id === selectedCourse);
  }, [rows, selectedCourse]);

  const filteredRows = useMemo(() => {
    if (!search) return courseScripts;
    const s = search.toLowerCase();
    return courseScripts.filter(
      (r) =>
        r.student_id?.toString().includes(s) ||
        r.exam_barcode_id?.toString().includes(s) ||
        r.script_marks?.toString().includes(s) ||
        (r.remarks || "").toLowerCase().includes(s) ||
        (r.evaluation_status || "").toLowerCase().includes(s)
    );
  }, [search, courseScripts]);

  // PDF
  const handleViewPDF = (row) => {
    const url = `${API_BASE_URL}/${row.script_pdf_path}`;
    window.open(url, "_blank");
  };

  const handleEndEvaluation = (timeTakenMinutes) => {
    // Optional: call API to save time taken
    axios.post(`${API_BASE_URL}/api/script-valuation/update-time`, {
      valuation_script_detail_id: selectedScript.valuation_script_detail_id,
      time_taken_for_valuation: timeTakenMinutes,
    });

    setSelectedScript(null);
    fetchScripts(); // reload
  };

  const scriptColumns = [
    {
      field: "sno",
      headerName: "S.No",
      flex: 0.3,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    { field: "student_id", headerName: "Student ID", flex: 0.7 },
    { field: "exam_barcode_id", headerName: "Barcode", flex: 1 },
    { field: "script_marks", headerName: "Marks", flex: 0.5 },
    { field: "time_taken_for_valuation", headerName: "Time (min)", flex: 0.5 },
    {
      field: "evaluation_status",
      headerName: "Valuation Status",
      flex: 1.2,
      renderCell: (params) => {
        const status = params.value;

        const statusColorMap = {
          PENDING: "warning", // orange
          VERIFIED: "success", // green
          COMPLETED: "info", // blue
        };

        return (
          <Chip
            label={status}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 400,
              minWidth: 90,
              justifyContent: "center",

              backgroundColor:
                status === "PENDING"
                  ? "#fff3e0" // light orange
                  : status === "VERIFIED"
                  ? "#e8f5e9" // light green
                  : status === "COMPLETED"
                  ? "#e3f2fd" // light blue
                  : "#f5f5f5",

              color:
                status === "PENDING"
                  ? "#ed6c02"
                  : status === "VERIFIED"
                  ? "#2e7d32"
                  : status === "COMPLETED"
                  ? "#0288d1"
                  : "#757575",

              borderColor:
                status === "PENDING"
                  ? "#ed6c02"
                  : status === "VERIFIED"
                  ? "#2e7d32"
                  : status === "COMPLETED"
                  ? "#0288d1"
                  : "#bdbdbd",
            }}
          />
        );
      },
    },

    //{ field: "remarks", headerName: "Remarks", flex: 1.2 },

    /*{
      field: "pdf",
      headerName: "PDF",
      flex: 0.5,
      renderCell: (params) => (
        <IconButton color="error" size="small" onClick={() => handleViewPDF(params.row)}>
          <PictureAsPdfIcon />
        </IconButton>
      ),
    },*/
    {
      field: "actions",
      headerName: "Action",
      flex: 0.6,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const status = params.row.evaluation_status;

        // 🔵 PENDING → Evaluate
        if (status === "PENDING" && roleId == 6) {
          return (
            <IconButton
              color="primary"
              size="small"
              title="Evaluate"
              onClick={() => handleStartEvaluation(params.row)}
            >
              <PlayCircleIcon />
            </IconButton>
          );
        }

        // 🟢 COMPLETED → Verify
        if (status === "COMPLETED" && roleId == 1) {
          return (
            <IconButton
              color="success"
              size="small"
              title="Verify"
              onClick={() => handleStartEvaluation(params.row)}
            >
              <VerifiedIcon />
            </IconButton>
          );
        }

        // ✅ VERIFIED → No action / locked
        return <VerifiedIcon color="disabled" titleAccess="Already Verified" />;
      },
    },
  ];
  const renderCountChip = (value, bg, color) => (
    <Chip
      label={value ?? 0}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "16px",
        minWidth: 60,
        backgroundColor: bg,
        color: color,
        justifyContent: "center",
      }}
    />
  );
  return (
    <Box p={2} sx={{ height: 560, width: "100%", gap: 1 }}>
      <Paper elevation={1}>
        {/* === Script Viewer === */}
        {selectedScript ? (
          <EvaluationViewer
            pdfUrl={`${selectedScript.script_pdf_path}`}
            questionPaperUrl={`${API_BASE_URL}/api/script-valuation/get-question-paper/${user.college_id}/${selectedScript.timetable_id}`}
            schemePaperUrl={`${API_BASE_URL}/api/script-valuation/get-scheme-paper/${user.college_id}/${selectedScript.timetable_id}`}
            startTime={startTime} // ⏱ Pass start time
            //onFinish={handleEndEvaluation} // ⏱ Callback for finish
            onClose={() => {
              setSelectedScript(null);
              fetchScripts();
            }}
            valuation_script_detail_id={selectedScript.valuation_script_detail_id}
          />
        ) : !selectedCourse ? (
          /* === LAYER 1: COURSE LIST === */
          <Box sx={{ height: 460, width: "100%", flexWrap: "wrap", gap: 2 }}>
            <Toolbar>
              <Typography variant="h6">VALUATION COURSES</Typography>
            </Toolbar>
            <Box sx={{ height: 460, width: "100%", gap: 1 }}>
              <DataGrid
                rows={groupedCourses}
                getRowId={(row) => row.exam_course_id}
                loading={loading}
                columns={[
                  {
                    field: "id",
                    headerName: "S.No",
                    sortable: false,
                    filterable: false,
                    width: 80,
                    align: "center",
                    headerAlign: "center",
                    valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
                  },
                  { field: "exam_course_id", headerName: "Course ID", width: 100 },
                  { field: "course_code", headerName: "Course Code", minWidth: 150 },
                  { field: "course_name", headerName: "Course Name", flex: 1, minWidth: 200 },

                  {
                    field: "total",
                    headerName: "Scripts",
                    minWidth: 100,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => renderCountChip(params.value, "#e3f2fd", "#0288d1"), // blue
                  },
                  {
                    field: "pending",
                    headerName: "Pending",
                    minWidth: 100,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => renderCountChip(params.value, "#fff3e0", "#ed6c02"), // orange
                  },
                  {
                    field: "completed",
                    headerName: "Completed",
                    minWidth: 110,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => renderCountChip(params.value, "#e8f5e9", "#2e7d32"), // green
                  },
                  {
                    field: "verified",
                    headerName: "Verified",
                    minWidth: 100,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => renderCountChip(params.value, "#ede7f6", "#5e35b1"), // purple
                  },

                  {
                    field: "action",
                    headerName: "Start",
                    flex: 1,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => {
                      const { total, verified } = params.row;

                      // 👉 If all scripts are verified, do NOT show button
                      if (Number(total) === Number(verified)) {
                        return (
                          <Typography variant="body2" color="success.main" fontWeight={500}>
                            Completed
                          </Typography>
                        );
                      }

                      return (
                        <Button
                          variant="contained"
                          startIcon={<PlayCircleIcon />}
                          onClick={() => {
                            setSelectedCourse(params.row.exam_course_id);
                            setSelectedCourseCode(params.row.course_code);
                            setSelectedCourseName(params.row.course_name);
                          }}
                          sx={{
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            "&:hover": { backgroundColor: "#115293" },
                            borderRadius: "8px",
                            fontSize: "14px",
                            padding: "4px 12px",
                            textTransform: "none",
                          }}
                        >
                          Start
                        </Button>
                      );
                    },
                  },
                ]}
                paginationMode="server"
                rowsPerPageOptions={[5, 10, 25, 50]}
                disableSelectionOnClick
              />
            </Box>
          </Box>
        ) : (
          /* === LAYER 2: SCRIPT LIST FOR SELECTED COURSE === */
          <>
            <Toolbar sx={{ gap: 1 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                COURSE: {selectedCourseCode}-{selectedCourseName} — {courseScripts.length} Scripts
              </Typography>

              <Button
                variant="outlined"
                onClick={() => setSelectedCourse(null)}
                size="small"
                sx={{
                  backgroundColor: "#1976d2", // custom blue
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#115293",
                  },
                  borderRadius: "8px",
                  fontSize: "12px",
                  padding: "2px 12px",
                }}
              >
                Back{" "}
              </Button>

              <TextField
                size="small"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Toolbar>
            <Box sx={{ height: 460, width: "100%", flexWrap: "wrap" }}>
              <DataGrid
                rows={filteredRows}
                columns={scriptColumns}
                getRowId={(row) => row.valuation_script_detail_id}
                loading={loading}
                pageSize={pageSize}
                onPageSizeChange={(n) => setPageSize(n)}
                rowsPerPageOptions={[5, 10, 25, 50]}
                pagination
                disableRowSelectionOnClick
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
